import React from 'react';
import './PrivacyPolicy.scss';

const PrivacyPolicy = () => (
  <main className="legal-page" data-testid="privacy-page">
    <div className="legal-page__container">
      <h1 className="legal-page__title">Privacy Policy</h1>
      <p className="legal-page__effective">Effective Date: April 3, 2026</p>

      <section className="legal-page__section">
        <p>
          HaulSync ("we", "us", or "our") is committed to protecting your privacy. This Privacy
          Policy explains how we collect, use, disclose, and safeguard your information when you
          use the HaulSync website and mobile application (collectively, the "Service"). By using
          the Service, you consent to the practices described in this policy.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">1. Information We Collect</h2>
        <p>We may collect the following types of information:</p>

        <h3 className="legal-page__subsection-title">a) Information You Provide</h3>
        <ul className="legal-page__list">
          <li><strong>Account Information:</strong> Name, email address, phone number, business name, and password when you register.</li>
          <li><strong>Profile Information:</strong> Business type (Contractor or Carrier), equipment details, service areas, and profile photo.</li>
          <li><strong>WSIB Verification Data:</strong> WSIB clearance certificate details provided during carrier onboarding.</li>
          <li><strong>Payment Information:</strong> Billing details processed securely through our third-party payment processor. HaulSync does not store full payment card numbers.</li>
          <li><strong>Communications:</strong> Messages sent through our in-app messaging system or to our support team.</li>
        </ul>

        <h3 className="legal-page__subsection-title">b) Information Collected Automatically</h3>
        <ul className="legal-page__list">
          <li><strong>Location Data:</strong> With your permission, we collect device location data to facilitate job matching, show nearby hauls, and provide route-related features.</li>
          <li><strong>Device Information:</strong> Device type, operating system, app version, and unique device identifiers.</li>
          <li><strong>Usage Data:</strong> Pages visited, features used, search queries, booking history, and interaction logs.</li>
          <li><strong>Log Data:</strong> IP address, browser type, timestamps, and crash reports.</li>
        </ul>

        <h3 className="legal-page__subsection-title">c) Information from Third Parties</h3>
        <ul className="legal-page__list">
          <li>WSIB clearance verification from government or authorized verification services.</li>
          <li>Payment and fraud-detection data from our payment processors.</li>
        </ul>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">2. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul className="legal-page__list">
          <li>Create and manage your HaulSync account.</li>
          <li>Connect Contractors with WSIB-verified Carriers for hauling jobs.</li>
          <li>Process payments and apply the platform service fee.</li>
          <li>Verify WSIB compliance for carrier accounts.</li>
          <li>Send transactional notifications (booking confirmations, payment receipts, job updates).</li>
          <li>Send service-related communications and, with your consent, promotional updates.</li>
          <li>Improve and personalize the Service based on usage patterns.</li>
          <li>Detect, prevent, and investigate fraud or security incidents.</li>
          <li>Comply with legal obligations under Ontario and Canadian law.</li>
        </ul>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">3. How We Share Your Information</h2>
        <p>We do not sell your personal information. We may share your data in the following circumstances:</p>
        <ul className="legal-page__list">
          <li><strong>Between Platform Users:</strong> Profile information (business name, rating, equipment type) is shared between Contractors and Carriers as needed to facilitate bookings.</li>
          <li><strong>Service Providers:</strong> We share data with trusted third-party vendors (payment processors, cloud hosting, analytics, push notification services) solely to operate the Service.</li>
          <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, court order, or to protect the rights and safety of HaulSync, our users, or the public.</li>
          <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred to the successor entity.</li>
        </ul>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">4. Location Data</h2>
        <p>
          The HaulSync mobile app requests access to your device's location to enable features
          such as job matching and proximity-based search. Location access is optional — you may
          deny this permission, but some features may be limited. You can revoke location
          permissions at any time through your device settings. We do not share precise location
          data with third parties for advertising purposes.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">5. Data Retention</h2>
        <p>
          We retain your personal information for as long as your account is active or as needed
          to provide the Service. If you close your account, we will retain certain data as
          required by law or for legitimate business purposes (e.g., transaction records,
          dispute resolution), after which it will be securely deleted or anonymized.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">6. Data Security</h2>
        <p>
          We implement industry-standard security measures including encryption in transit
          (HTTPS/TLS), access controls, and secure cloud infrastructure to protect your
          information. However, no method of transmission over the internet is 100% secure, and
          we cannot guarantee absolute security.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">7. Your Rights (PIPEDA &amp; Ontario Privacy Law)</h2>
        <p>
          Under Canada's Personal Information Protection and Electronic Documents Act (PIPEDA)
          and applicable Ontario privacy legislation, you have the right to:
        </p>
        <ul className="legal-page__list">
          <li><strong>Access</strong> the personal information we hold about you.</li>
          <li><strong>Correct</strong> inaccurate or incomplete information.</li>
          <li><strong>Withdraw consent</strong> to certain uses of your data (which may affect your ability to use the Service).</li>
          <li><strong>Request deletion</strong> of your personal data, subject to legal retention requirements.</li>
          <li><strong>Lodge a complaint</strong> with the Office of the Privacy Commissioner of Canada.</li>
        </ul>
        <p>
          To exercise any of these rights, please contact us using the details in Section 11.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">8. Children's Privacy</h2>
        <p>
          The HaulSync Service is not intended for individuals under the age of 18. We do not
          knowingly collect personal information from minors. If we become aware that we have
          inadvertently collected data from a minor, we will promptly delete it.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">9. Cookies and Tracking Technologies</h2>
        <p>
          Our website uses cookies and similar technologies to enhance your browsing experience,
          analyze usage, and remember your preferences. You can control cookie settings through
          your browser. Our mobile app may use analytics SDKs (such as Firebase Analytics) to
          help us understand app performance and user behavior; this data is aggregated and
          anonymized where possible.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">10. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy periodically. We will notify you of significant
          changes by posting the updated policy on this page and updating the effective date.
          Your continued use of the Service after changes are posted constitutes your acceptance
          of the updated policy.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">11. Contact Us</h2>
        <p>
          If you have questions, concerns, or requests regarding this Privacy Policy or our
          data practices, please contact our Privacy Officer:
        </p>
        <address className="legal-page__address">
          <strong>HaulSync Privacy Officer</strong><br />
          Ontario, Canada<br />
          Email: <a href="mailto:privacy@haulsync.ca">privacy@haulsync.ca</a>
        </address>
      </section>
    </div>
  </main>
);

export default PrivacyPolicy;
